import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import styled from 'styled-components'

import { QUERY_KEY } from '../../../constants'
import type { operations } from '../../../constants/schema'

import { getHomeTerms, getHomeTermsDetail } from '../../../api/homeTerms'

import { ModalWrap } from '../../../components/modal/ModalWrap'
import { Content } from '../../../components/container/Content'
import { AlignCenterItem, ColumnItem } from '../../../components/common'
import { Button } from '../../../components/button'
import { Text } from '../../../components/text'
import { Footer } from '../../../components/footer'
import { Spinner } from '../../../components/spinner/Spinner'
import { ArtipioContents, ArtipioContentItem } from '../../../components/contents'

import { Color } from '../../../theme/color'

import { ReactComponent as DropIcon } from '../../../assets/ico/common/down_18.svg'

type TermsType = Exclude<
  Required<operations['HomeTermsController_findAll']['parameters']>['query']['termsType'],
  undefined
>

interface HomeTermsModalProps {
  termsType: TermsType
  close: () => void
}

export function HomeTermsTermsModal({ termsType, close }: HomeTermsModalProps) {
  const [termsId, setTermsId] = useState<number | null>(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false)

  const {
    data: homeTerms,
    isPending: isHomeTermsPending,
    isError: isHomeTermsError,
    isSuccess,
  } = useInfiniteQuery({
    queryKey: [QUERY_KEY.homeTerms, { termsType }],
    queryFn: async ({ pageParam }) => await getHomeTerms({ termsType, offset: pageParam, size: 10 }),
    getNextPageParam: ({ last, offset, size }) => (last ? undefined : offset + size),
    initialPageParam: 0,
    select: (data) => ({
      ...data,
      pages: data.pages.map((page) => page.list).flat(),
    }),
  })

  const { data, isPending, isError } = useQuery({
    queryKey: [QUERY_KEY.homeTerms, { termsType, termsId }],
    queryFn: () => getHomeTermsDetail(termsId as number),
    enabled: typeof termsId === 'number',
  })

  useEffect(() => {
    if (!isSuccess || typeof homeTerms?.pages[0].id !== 'number') return

    setTermsId(homeTerms?.pages[0].id)
  }, [isSuccess])

  if (isHomeTermsPending || isHomeTermsError || isPending || isError) {
    return (
      <ModalWrap>
        <LoadingWrap>
          <Spinner />
        </LoadingWrap>
      </ModalWrap>
    )
  }

  const versionTitle = termsType === 'TERMS' ? '서비스 이용약관' : '개인정보처리방침'

  const toggleDropdown = () => setIsDropdownOpen((prevState) => !prevState)

  return (
    <ModalWrap>
      <StyledContent>
        <ArtipioContents>
          {data.contents?.map((content, i) => {
            const isFirstImg = content.type === 'image' && i === 0
            const isLastImg = content.type === 'image' && i === (data.contents?.length ?? 0) - 1
            return <ArtipioContentItem key={i} {...content} isFirstImg={isFirstImg} isLastImg={isLastImg} />
          })}
        </ArtipioContents>
        <ListBox>
          <Text size={14} weight={600}>
            이전 {versionTitle} 보기
          </Text>
          <Dropdown $gap={8} isDropdownOpen={isDropdownOpen}>
            <AlignCenterItem $gap={4} onClick={toggleDropdown}>
              <Text size={14} weight={600}>
                {dayjs(data.createdDate).format('YYYY-MM-DD')}
              </Text>
              <DropIcon />
            </AlignCenterItem>
            {isDropdownOpen ? (
              homeTerms.pages.filter((data) => data.id !== termsId).length ? (
                homeTerms.pages
                  .filter((data) => data.id !== termsId)
                  .map(({ id, createdDate }) => {
                    const handleTermsId = () => setTermsId(id)
                    return (
                      <Text
                        key={id}
                        size={14}
                        lineHeight={22}
                        $textDecoration="underline"
                        $cursor="pointer"
                        onClick={handleTermsId}
                      >
                        {dayjs(createdDate).format('YYYY-MM-DD')}
                      </Text>
                    )
                  })
              ) : (
                <Text size={14} lineHeight={22}>
                  이전 {versionTitle}이 없어요
                </Text>
              )
            ) : null}
          </Dropdown>
        </ListBox>
        <Footer>
          <Button size="large" block={true} onClick={close}>
            확인
          </Button>
        </Footer>
      </StyledContent>
    </ModalWrap>
  )
}

const LoadingWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: ${Color.white};
`

const StyledContent = styled(Content)`
  justify-content: space-between;
  > *:first-child {
    flex-shrink: 0;
  }
`

const ListBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 24px;
  padding-bottom: 108px;
  background-color: ${Color.gray_2};
`

const Dropdown = styled(ColumnItem)<{ isDropdownOpen: boolean }>`
  overflow: hidden;
  > ${AlignCenterItem} {
    width: fit-content;
    cursor: pointer;
    > svg {
      ${({ isDropdownOpen }) => (isDropdownOpen ? 'transform: rotateZ(180deg);' : '')}
    }
  }
  > span {
    width: fit-content;
  }
`
